import { Icon } from "@iconify/react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const Color = {
  themeBlue: "#4ea4f5",
  themePurple: "#8b5cf6",
  themeGreen: "#22c55e",
  themeOrange: "#f59e0b",
  textPrimary: "#000",
  borderLight: "#e0e0e0",
  white: "#fff",
  bgLight: "#f8f9fa",
};

interface FeatureSectionInstantToolsProps {
  showDescription?: boolean;
}

type AnimationPhase =
  | "idle"
  | "showTools"
  | "hoverSummary"
  | "clickSummary"
  | "generating"
  | "showResult"
  | "holdResult";

interface Tool {
  id: string;
  icon: string;
  labelKey: string;
  color: string;
}

const FeatureSectionInstantTools: React.FC<FeatureSectionInstantToolsProps> = ({ showDescription = true }) => {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<AnimationPhase>("idle");
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState(0);
  const [showBulletPoints, setShowBulletPoints] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [cursorClicking, setCursorClicking] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const animationStartedRef = useRef<boolean>(false);
  const bulletPointsShownRef = useRef<boolean>(false);

  // Only animate when 90% of card is visible (Google's standard)
  const isInView = useInView(cardRef, { amount: 0.9 });

  const CARD_WIDTH = 580;
  const CARD_HEIGHT = 440;

  const tools: Tool[] = [
    { id: "summary", icon: "lucide:file-text", labelKey: "modern.instantTools.tools.summary", color: Color.themeBlue },
    { id: "quiz", icon: "lucide:clipboard-check", labelKey: "modern.instantTools.tools.quiz", color: Color.themeGreen },
    { id: "flashcards", icon: "lucide:layers", labelKey: "modern.instantTools.tools.flashcards", color: Color.themeOrange },
    { id: "notes", icon: "lucide:edit-3", labelKey: "modern.instantTools.tools.notes", color: Color.themePurple },
  ];

  // Animation sequence - only animates Summary
  useEffect(() => {
    if (!isInView) {
      animationStartedRef.current = false;
      return;
    }

    if (animationStartedRef.current) return;
    animationStartedRef.current = true;

    let isCancelled = false;

    const wait = (ms: number) => new Promise<boolean>(resolve => {
      setTimeout(() => resolve(!isCancelled), ms);
    });

    const runAnimation = async () => {
      while (!isCancelled) {
        // Reset state
        setPhase("idle");
        setHoveredTool(null);
        setSelectedTool(null);
        setShowResult(false);
        setGeneratingProgress(0);
        setCursorVisible(false);
        setCursorClicking(false);
        if (!bulletPointsShownRef.current) {
          setShowBulletPoints(false);
        }

        if (!await wait(500)) return;

        // ===== Show tools =====
        setPhase("showTools");
        if (!await wait(800)) return;

        // ===== Cursor enters and moves to Summary button (top-left of grid) =====
        setCursorVisible(true);
        setCursorPosition({ x: CARD_WIDTH + 30, y: CARD_HEIGHT / 2 });
        if (!await wait(100)) return;

        // Move to Summary button (top-left in 2x2 grid)
        // Grid starts at: padding 24px, header ~50px, so grid top ~74px
        // Each grid cell is roughly (CARD_WIDTH - 48 - 12) / 2 = 230px wide
        // Summary button center: x = 24 + 115 = 139, y = 74 + 24 = 98
        setCursorPosition({ x: 150, y: 120 });
        if (!await wait(400)) return;

        // ===== Hover effect on Summary =====
        setPhase("hoverSummary");
        setHoveredTool("summary");
        if (!await wait(500)) return;

        // ===== Click Summary =====
        setPhase("clickSummary");
        setCursorClicking(true);
        if (!await wait(150)) return;
        setCursorClicking(false);
        setSelectedTool("summary");
        setHoveredTool(null);
        if (!await wait(200)) return;

        // Hide cursor
        setCursorVisible(false);
        if (!await wait(100)) return;

        // ===== Generating animation =====
        setPhase("generating");
        for (let i = 0; i <= 100; i += 5) {
          if (isCancelled) return;
          setGeneratingProgress(i);
          if (!await wait(25)) return;
        }
        if (!await wait(200)) return;

        // ===== Show result =====
        setPhase("showResult");
        setShowResult(true);
        if (!await wait(300)) return;

        // Show bullet points
        setShowBulletPoints(true);
        bulletPointsShownRef.current = true;

        // ===== Hold result =====
        setPhase("holdResult");
        if (!await wait(5000)) return;

        // Pre-reset cursor entry
        setCursorVisible(true);
        setCursorPosition({ x: CARD_WIDTH + 30, y: CARD_HEIGHT / 2 });
        if (!await wait(300)) return;
        setCursorPosition({ x: CARD_WIDTH / 2 + 50, y: CARD_HEIGHT / 2 });
        if (!await wait(700)) return;
      }
    };

    runAnimation();

    return () => {
      isCancelled = true;
      animationStartedRef.current = false;
    };
  }, [isInView]);

  return (
    <section className={`${showDescription ? 'py-8 md:py-20' : 'py-0'} bg-white overflow-hidden`}>
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div className={`${showDescription ? 'grid md:grid-cols-2 gap-12 lg:gap-20 items-center max-w-7xl mx-auto' : 'flex justify-center'}`}>
          {/* Left - Text Content */}
          {showDescription && (
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ backgroundColor: `${Color.themePurple}15` }}
              >
                <Icon icon="lucide:zap" style={{ color: Color.themePurple }} />
                <span style={{ color: Color.themePurple }} className="font-medium text-sm">
                  {t("modern.instantTools.badge")}
                </span>
              </div>

              <h2 className="text-11xl md:text-17xl lg:text-29xl font-bold text-gray-900 mb-6 leading-tight mt-0">
                {t("modern.instantTools.headline")}
              </h2>

              <p className="text-lg md:text-xl mb-8 leading-relaxed" style={{ color: "#726F6F" }}>
                {t("modern.instantTools.description")}
              </p>

              {/* Mobile bullets - always visible */}
              <div className="block md:hidden space-y-4">
                {[
                  { icon: "lucide:settings-2", textKey: "modern.instantTools.bullets.customizable" },
                  { icon: "lucide:quote", textKey: "modern.instantTools.bullets.citations" },
                  { icon: "lucide:brain", textKey: "modern.instantTools.bullets.interactive" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${Color.themePurple}15` }}
                    >
                      <Icon icon={item.icon} className="text-lg" style={{ color: Color.themePurple }} />
                    </div>
                    <span className="text-base text-gray-700">{t(item.textKey)}</span>
                  </div>
                ))}
              </div>

              {/* Desktop bullets - animated */}
              <div className="hidden md:block" style={{ minHeight: 140 }}>
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showBulletPoints ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {[
                    { icon: "lucide:settings-2", textKey: "modern.instantTools.bullets.customizable" },
                    { icon: "lucide:quote", textKey: "modern.instantTools.bullets.citations" },
                    { icon: "lucide:brain", textKey: "modern.instantTools.bullets.interactive" },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: showBulletPoints ? 1 : 0, x: showBulletPoints ? 0 : -20 }}
                      transition={{ delay: showBulletPoints ? index * 0.15 : 0, duration: 0.4 }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${Color.themePurple}15` }}
                      >
                        <Icon icon={item.icon} className="text-lg" style={{ color: Color.themePurple }} />
                      </div>
                      <span className="text-base text-gray-700">{t(item.textKey)}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Right - Visual Demo */}
          <motion.div
            initial={{ opacity: 0, x: showDescription ? 50 : 0 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-2 px-4 md:px-0"
          >
            <div className="relative w-full max-w-[580px] mx-auto">
              {/* Floating badge */}
              <motion.div
                className="px-4 py-2 rounded-full shadow-lg flex items-center gap-2 absolute z-30"
                style={{ top: -6, right: -6, backgroundColor: Color.themePurple }}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon icon="lucide:sparkles" className="text-white" />
                <span className="text-white text-sm font-medium">{t("modern.instantTools.floatingBadge")}</span>
              </motion.div>

              {/* Main Card - White theme */}
              <div
                ref={cardRef}
                className="rounded-3xl overflow-hidden shadow-2xl relative"
                style={{
                  backgroundColor: Color.white,
                  border: `1px solid ${Color.borderLight}`,
                  height: CARD_HEIGHT,
                }}
              >
                <div className="absolute inset-0 p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <Icon icon="lucide:folder" className="text-gray-400" />
                      <span className="font-semibold text-gray-700">{t("modern.instantTools.myNotes")}</span>
                    </div>
                    <div
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: Color.bgLight, border: `1px solid ${Color.borderLight}` }}
                    >
                      <Icon icon="lucide:plus" className="text-gray-500 text-sm" />
                      <span className="text-gray-600 text-sm">{t("modern.instantTools.addNote")}</span>
                    </div>
                  </div>

                  {/* Tools Grid - 2x2 */}
                  <AnimatePresence>
                    {!showResult && (
                      <motion.div
                        className="grid grid-cols-2 gap-3 mb-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                      >
                        {tools.map((tool, index) => {
                          const isHovered = hoveredTool === tool.id;
                          const isSelected = selectedTool === tool.id;

                          return (
                            <motion.div
                              key={tool.id}
                              className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer"
                              style={{
                                backgroundColor: isSelected ? tool.color : Color.white,
                                border: isHovered ? `2px solid ${tool.color}` : `2px solid ${Color.borderLight}`,
                                boxShadow: isHovered ? `0 0 20px ${tool.color}30` : "0 2px 8px rgba(0,0,0,0.05)",
                              }}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{
                                opacity: 1,
                                y: 0,
                                scale: isHovered ? 1.05 : isSelected ? 0.95 : 1,
                              }}
                              transition={{ delay: index * 0.1, duration: 0.3 }}
                            >
                              <Icon
                                icon={tool.icon}
                                className="text-xl"
                                style={{ color: isSelected ? Color.white : tool.color }}
                              />
                              <span
                                className="text-sm font-medium"
                                style={{ color: isSelected ? Color.white : Color.textPrimary }}
                              >
                                {t(tool.labelKey)}
                              </span>
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Generating Progress */}
                  <AnimatePresence>
                    {phase === "generating" && (
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center bg-white"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <div className="text-center">
                          <motion.div
                            className="w-16 h-16 rounded-full border-4 border-t-transparent mx-auto mb-4"
                            style={{ borderColor: `${Color.themeBlue}30`, borderTopColor: Color.themeBlue }}
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                          <p className="text-gray-600 text-sm font-medium">{t("modern.instantTools.generating")}</p>
                          <div
                            className="w-48 h-1.5 rounded-full mt-3 mx-auto overflow-hidden"
                            style={{ backgroundColor: Color.bgLight }}
                          >
                            <motion.div
                              className="h-full rounded-full"
                              style={{ backgroundColor: Color.themeBlue, width: `${generatingProgress}%` }}
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Result Card */}
                  <AnimatePresence>
                    {showResult && (
                      <motion.div
                        className="rounded-xl overflow-hidden"
                        style={{ backgroundColor: Color.white, border: `1px solid ${Color.borderLight}`, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ type: "spring" as const, stiffness: 200, damping: 20 }}
                      >
                        {/* Result Header */}
                        <div
                          className="px-4 py-3 flex items-center gap-3"
                          style={{ borderBottom: `1px solid ${Color.borderLight}` }}
                        >
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${Color.themeBlue}15` }}
                          >
                            <Icon icon="lucide:file-text" className="text-sm" style={{ color: Color.themeBlue }} />
                          </div>
                          <div>
                            <p className="text-gray-900 font-semibold text-sm">{t("modern.instantTools.results.summary.title")}</p>
                          </div>
                          <div className="ml-auto flex items-center gap-1 px-2 py-1 rounded-full" style={{ backgroundColor: `${Color.themeGreen}15` }}>
                            <Icon icon="lucide:check-circle" className="text-xs" style={{ color: Color.themeGreen }} />
                            <span className="text-xs font-medium" style={{ color: Color.themeGreen }}>{t("modern.instantTools.generated")}</span>
                          </div>
                        </div>
                        {/* Result Content - 3 paragraphs with inline citations */}
                        <div className="p-4 space-y-3">
                          <p className="text-gray-700 text-sm leading-relaxed">
                            {t("modern.instantTools.results.summary.paragraph1")}
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ml-1" style={{ backgroundColor: `${Color.themeBlue}15`, color: Color.themeBlue }}>
                              <Icon icon="lucide:file-text" className="text-xs" />
                              P.5
                            </span>
                          </p>
                          <p className="text-gray-700 text-sm leading-relaxed">
                            {t("modern.instantTools.results.summary.paragraph2")}
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ml-1" style={{ backgroundColor: `${Color.themePurple}15`, color: Color.themePurple }}>
                              <Icon icon="lucide:mic" className="text-xs" />
                              2:30
                            </span>
                          </p>
                          <p className="text-gray-700 text-sm leading-relaxed">
                            {t("modern.instantTools.results.summary.paragraph3")}
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ml-1" style={{ backgroundColor: "#ff000015", color: "#ff0000" }}>
                              <Icon icon="lucide:play-circle" className="text-xs" />
                              5:15
                            </span>
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Mouse Cursor - Hidden on mobile */}
                  <AnimatePresence>
                    {cursorVisible && (
                      <motion.div
                        className="absolute z-50 pointer-events-none hidden md:block"
                        style={{ left: 0, top: 0 }}
                        initial={{ opacity: 0 }}
                        animate={{
                          opacity: 1,
                          x: cursorPosition.x,
                          y: cursorPosition.y,
                          scale: cursorClicking ? 0.8 : 1,
                        }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "spring" as const, stiffness: 200, damping: 25 }}
                      >
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87a.5.5 0 0 0 .35-.85L6.35 2.86a.5.5 0 0 0-.85.35Z"
                            fill="#fff"
                            stroke="#000"
                            strokeWidth="1.5"
                          />
                        </svg>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSectionInstantTools;
